import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Ruler, ClipboardCheck, Layers, Layout, Lock, ArrowRight, ChevronDown,
} from "lucide-react";
import { Badge } from "../components/ui/badge";

const MODULES = [
  {
    id: "paintmeasure", name: "PaintMeasure™",
    descShort: "Opmeten en offreren voor schilders en afwerkingsbedrijven — van binnenschilderwerk tot gevels, schrijnwerk, trappen en meerwerken.",
    descFull: {
      intro: "PaintMeasure™ helpt schilders en afwerkingsbedrijven om sneller en professioneler opmetingen en offertes te maken.",
      listTitle: "Werkdomeinen:",
      list: [
        "Binnenschilderwerken: schilderwerken, behangwerken en schilderstechnieken",
        "Binnenschrijnwerk",
        "Buitenschrijnwerk",
        "Gevelwerken / buitenschilderwerken",
        "Trapwerken",
        "Meerwerken / regie",
      ],
      outro: "Alles blijft gekoppeld aan centrale klanten, projecten, sjablonen, prijslijst en professionele PDF-offertes.",
    },
    icon: Ruler, color: "bg-[#D96C4A]", active: true,
    to: "/projecten", cta: "Selecteer project",
  },
  {
    id: "floormeasure", name: "FloorMeasure™",
    descShort: "Opmeten en offreren van vloerwerken zoals parket, laminaat, tegels, vinyl, plinten en voorbereiding.",
    icon: Layout, color: "bg-[#8A9A86]", active: false,
  },
  {
    id: "plastermeasure", name: "PlasterMeasure™",
    descShort: "Opmeten en offreren van pleisterwerken, plafonds, voorzetwanden en afwerkingslagen.",
    icon: Layers, color: "bg-[#D4A373]", active: false,
  },
  {
    id: "emmainspect", name: "InspectMeasure™",
    descShort: "Plaatsbeschrijvingen, opleveringen en inspectiedossiers overzichtelijk vastleggen met foto's en rapporten.",
    icon: ClipboardCheck, color: "bg-[#2C2C2A]", active: false,
  },
];

export default function Modules() {
  // Set van uitgeklapte module-ids. Werkt per kaart, onafhankelijk.
  const [expanded, setExpanded] = useState(() => new Set());

  const toggleExpanded = (id) => (e) => {
    // Voorkom dat de klik naar de <Link> propageert (die naar /projecten navigeert).
    e.preventDefault();
    e.stopPropagation();
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-8" data-testid="modules-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-[#5D7259]">EmmaForge™ Suite</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-[#2C2C2A] mt-2 leading-tight">Modules</h1>
        <p className="text-[#4F4F4D] mt-2 max-w-2xl">
          Alle modules delen dezelfde klanten-, projecten-, prijslijst- en offerte-engine.
          Activeer wat je nodig hebt — uitbreiding gebeurt zonder migratie.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {MODULES.map((m) => {
          const Icon = m.icon;
          const isExpanded = expanded.has(m.id);
          const hasFull = !!m.descFull;
          const fullId = `module-desc-full-${m.id}`;

          return (
            <div
              key={m.id}
              className={`emma-card relative overflow-hidden transition-all ${m.active ? "hover:-translate-y-1" : "opacity-70"}`}
              data-testid={`module-card-${m.id}`}
            >
              <div className={`absolute -right-12 -top-12 w-40 h-40 ${m.color} opacity-10 rounded-full blur-2xl`} />
              <div className={`relative w-14 h-14 rounded-2xl ${m.color} flex items-center justify-center text-white`}>
                <Icon className="w-6 h-6" />
              </div>
              <div className="mt-5 flex items-center gap-2 flex-wrap">
                <h3 className="font-heading font-bold text-xl text-[#2C2C2A]">{m.name}</h3>
                {!m.active && (
                  <Badge className="bg-[#F0EFEA] text-[#4F4F4D] gap-1">
                    <Lock className="w-3 h-3" /> binnenkort
                  </Badge>
                )}
              </div>

              {/* Korte, uitnodigende omschrijving */}
              <p className="text-sm text-[#4F4F4D] mt-2" data-testid={`module-desc-short-${m.id}`}>
                {m.descShort}
              </p>

              {/* Uitgeklapte extra uitleg — altijd in DOM (voor geldige aria-controls),
                  verborgen via `hidden` wanneer niet expanded. */}
              {hasFull && (
                <div
                  id={fullId}
                  hidden={!isExpanded}
                  className="mt-3 text-sm text-[#4F4F4D] space-y-2"
                  data-testid={`module-desc-full-${m.id}`}
                >
                  {m.descFull.intro && <p>{m.descFull.intro}</p>}
                  {m.descFull.listTitle && (
                    <p className="font-semibold text-[#2C2C2A]">{m.descFull.listTitle}</p>
                  )}
                  {Array.isArray(m.descFull.list) && m.descFull.list.length > 0 && (
                    <ul className="list-disc list-inside space-y-0.5 marker:text-[#D96C4A]">
                      {m.descFull.list.map((item, i) => (
                        <li key={i}>{item}</li>
                      ))}
                    </ul>
                  )}
                  {m.descFull.outro && <p>{m.descFull.outro}</p>}
                </div>
              )}

              {/* Toggle knop (enkel als er een descFull is).
                  `relative z-10` zorgt dat de knop boven de absolute Link-overlay ligt
                  zodat klikken op de knop niet de kaart-navigatie triggert. */}
              {hasFull && (
                <button
                  type="button"
                  onClick={toggleExpanded(m.id)}
                  className="relative z-10 mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#5D7259] hover:text-[#D96C4A] transition"
                  data-testid={`module-toggle-${m.id}`}
                  aria-expanded={isExpanded}
                  aria-controls={fullId}
                >
                  <ChevronDown
                    className={`w-3.5 h-3.5 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                  />
                  {isExpanded ? "Minder tonen" : "Lees meer"}
                </button>
              )}

              {m.active && (
                <div className="mt-5 flex items-center gap-1 text-[#D96C4A] font-semibold text-sm">
                  {m.cta} <ArrowRight className="w-4 h-4" />
                </div>
              )}

              {/* Kaart-brede navigatielink (stretched-link overlay).
                  Enkel voor actieve modules; ligt op z-0 zodat interactieve
                  kinderen (bv. de toggle-knop met z-10) er bovenop staan.
                  Vermijdt de HTML-fout van <button> nested in <a>. */}
              {m.active && (
                <Link
                  to={m.to}
                  aria-label={`Open ${m.name}`}
                  className="absolute inset-0 z-0 rounded-2xl focus-visible:ring-2 focus-visible:ring-[#D96C4A] focus-visible:outline-none"
                  data-testid={`module-link-${m.id}`}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
